import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { DateTime } from "luxon";
import type { ExtractRouteParams } from "react-router";
import { useHistory, useParams } from "react-router-dom";
import { AUTH_URLS, createTimesheetUrl } from "src/pages/authenticated/Routes";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import {
  Asterisk,
  Breakpoints,
  Button,
  Card,
  Checkbox,
  DateInput,
  fontMedium,
  fontSmall,
  fontXSmall,
} from "swing-components";

import { AlertError, ContentSingleColumn, useModal } from "~components";
import { translate } from "~utils";

import { NonCompliantMealBreakActionSheet } from "../components/NonCompliantMealBreakActionSheet";
import { PaycodeReasons, type PaycodeReason } from "../components/PaycodeReasons";
import { TimecardEntryForm } from "../components/TimecardEntryForm";
import { getWeekRangeFromDateTime } from "../utils";

/***** Types *****/
// Represents the URL parameters for this page.
type URLParams = ExtractRouteParams<typeof AUTH_URLS.timesheetEntryEdit, string>;

/**
 * Page | TimesheetEntry
 *
 * TODO: Grab the index from the URL params and fetch the TimesheetData, pass it to the view
 * TODO: Rename component to `TimesheetDetailsPage` (or equivalent) and update
 * file to include suffix of `.page.tsx`
 */
export function TimesheetEntry() {
  const { indexId } = useParams<URLParams>();

  // Fetch timesheet data if we have an entryIndex
  const {
    data: timesheetData,
    error,
    isLoading,
  } = useQuery<{ data: Timesheet }, AxiosError>({
    queryKey: ["fetchTimesheet", indexId],
    // TODO: Update to fetch timesheet from API when ready
    // queryFn: () => GET("/api/sub/timesheet/{interval-id}", {
    //   pathParams: { "interval-id": params.entryIndex },
    // }),
    enabled: !!indexId,
  });

  // Mock data for testing - TODO:remove when API is ready
  const mockTimesheetData: Timesheet = [
    {
      type: "Timecard",
      date: "2024-01-15",
      request: {
        id: "req-1",
        startTime: "2024-01-15T08:00:00Z",
        endTime: "2024-01-15T16:00:00Z",
      },
      school: {
        id: "school-1",
        name: "Springfield Elementary",
        timeZone: "US/Pacific",
      },
      punches: [
        {
          id: "punch-1",
          ts: "2024-01-15T08:00:00Z",
          type: PunchType.PunchIn,
        },
        {
          id: "punch-2",
          ts: "2024-01-15T16:00:00Z",
          type: PunchType.PunchOut,
        },
      ],
    },
    {
      type: "Paycode",
      paycode: "Sick",
      id: "paycode-1",
      durationInMinutes: 480, // 8 hours
      date: "2024-01-16",
    },
  ];

  // FIXME: Use mock data for now, fallback to empty array
  const timesheet = timesheetData?.data || mockTimesheetData;

  // Find the specific entry by index if we have timesheet data and entryIndex
  const entry = indexId ? timesheet.find((_, index) => index.toString() === indexId) : undefined;

  // Create an error if entryIndex is provided but no matching entry is found
  const entryNotFoundError =
    indexId && !entry && !isLoading
      ? (new Error(`Timesheet entry with index ${indexId} not found`) as AxiosError)
      : null;

  // Combine API error with entry not found error
  const combinedError = error || entryNotFoundError;

  return <TimesheetEntryView entry={entry} error={combinedError} isLoading={isLoading} />;
}

type TimesheetEntryViewProps = {
  entry?: Timecard | Paycode;
  error?: AxiosError | null;
  isLoading?: boolean;
};

export function TimesheetEntryView(props: TimesheetEntryViewProps) {
  const { entry, isLoading, error: queryError } = props;

  /********* State ********/
  const [formData, setFormData] = useState<Timecard | Paycode | undefined>();
  const [isCertified, setIsCertified] = useState(false);
  const [missingBreakReason, setMissingBreakReason] = useState<string | undefined>();

  // Feature flag for missing break action sheet - hardcoded for now
  // TODO: Remove this once we align on how to manage the selected reason in UKG
  const isMissingBreakActionSheetEnabled = false;

  // Modal for missing break action sheet
  const { openModal, closeModal } = useModal({
    component: (
      <NonCompliantMealBreakActionSheet
        onDismiss={() => closeModal()}
        onSubmit={() => {
          console.log("Submitted with missing break reason:", missingBreakReason);
          // TODO: Submit to API when ready
          closeModal();
        }}
        goBackToForm={() => closeModal()}
        isSubmissionPending={false}
      />
    ),
    modalOptions: {
      cssClass: "action-sheet-modal",
      breakpoints: [0, 1],
      initialBreakpoint: 1,
    },
  });

  // Update formData when entry changes
  useEffect(() => {
    if (entry) {
      // Initialize with the existing entry data
      setFormData(entry);
    } else {
      // Reset to undefined for new Paycode entry
      setFormData(undefined);
    }
    // Reset certification when entry changes
    setIsCertified(false);
    // Reset missing break reason when entry changes
    setMissingBreakReason(undefined);
  }, [entry]);

  /********* Helpers ********/
  const { weekStart, weekEnd } = getWeekRangeFromDateTime(DateTime.now().toISODate());
  const nextDay = weekEnd.plus({ days: 1 }).toISODate();
  const previousDay = weekStart.minus({ days: 1 }).toISODate();

  // Extract date from entry
  function getEntryDate(entry: Timecard | Paycode | undefined) {
    if (!entry) {
      return DateTime.now().toISODate();
    }

    if (entry.type === "Paycode") {
      return entry.date;
    } else {
      if (entry.date) {
        return entry.date;
      }

      const punchInOrOut = entry.punches.find(
        (punch) => punch.type === PunchType.PunchIn || punch.type === PunchType.PunchOut,
      );
      return punchInOrOut ? DateTime.fromISO(punchInOrOut.ts).toISODate() : undefined;
    }
  }

  function handleDateChange(date: string) {
    setFormData((prev) => {
      if (!prev) return prev;
      return { ...prev, date };
    });
  }

  function handleFormDataChange(update: Partial<Timecard> | Partial<Paycode>) {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return prev;

      if (prev.type === "Timecard" && "punches" in update) {
        // Handle timecard updates
        return { ...prev, ...update } as Timecard;
      } else if (prev.type === "Paycode" && "durationInMinutes" in update) {
        // Handle paycode updates
        return { ...prev, ...update } as Paycode;
      }

      return prev;
    });
  }

  // handle paycode reason changes
  function handlePaycodeReasonChange(reason: PaycodeReason) {
    setFormData((prev) => {
      if (!prev || prev.type !== "Paycode") return prev;
      return { ...prev, paycode: reason } as Paycode;
    });
  }

  // Helper function to handle form submission
  function handleSubmit() {
    if (!formData) return;

    // For timecards, check if we need to show the missing break action sheet
    if (formData.type === "Timecard" && isMissingBreakActionSheetEnabled) {
      openModal();
      return;
    }

    // Otherwise, proceed with normal submission
    console.log("Submitting form data:", formData);
    // TODO: Submit to API when ready
  }

  // Check if form is valid
  const isFormValid = Boolean(
    formData &&
      isCertified &&
      formData.date &&
      (formData.type === "Timecard"
        ? formData.punches.some((punch) => punch.type === PunchType.PunchIn) &&
          formData.punches.some((punch) => punch.type === PunchType.PunchOut)
        : formData.type === "Paycode" &&
          formData.paycode &&
          formData.durationInMinutes &&
          formData.durationInMinutes > 0),
  );

  // TODO: Update to use correct title based on entry type
  return (
    <ScorePage isLoading={isLoading} hasBack title="Timesheet Entry">
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 24,
            padding: 16,
            [Breakpoints.mediumDeviceAndUp]: { padding: 0 },
          }}
        >
          {/* Error */}
          {/* TODO: Update to show if there is a mutation error as well */}
          {queryError && <AlertError />}

          {/* Display the entry if it exists */}
          {entry && (
            <>
              {/* If it's timcard, show the request details */}
              {entry.type === "Timecard" && (
                <RequestDetails
                  schoolName={entry.school.name}
                  date={entry.date}
                  startTime={entry.request.startTime}
                  endTime={entry.request.endTime}
                />
              )}
              {/* If it's paycode, show the reasons */}
              {entry.type === "Paycode" && (
                <PaycodeReasons
                  reason={formData?.type === "Paycode" ? formData.paycode : undefined}
                  onReasonChange={handlePaycodeReasonChange}
                />
              )}

              {/* Date */}
              {/* TODO: Double check disabled date range logic */}
              <Date
                value={getEntryDate(entry)}
                onChange={handleDateChange}
                disabledDateRanges={[
                  { start: "1900-01-01", end: previousDay },
                  { start: nextDay, end: "2100-12-31" },
                ]}
              />

              {/* Time Input Form */}
              {/* The props and stuff is based on the entry type */}
              {entry.type === "Timecard" && (
                <TimecardEntryForm
                  entryType="Timecard"
                  timecardPunches={entry.punches}
                  onChange={handleFormDataChange}
                />
              )}
              {entry.type === "Paycode" && (
                <TimecardEntryForm
                  entryType="Paycode"
                  paycodeDurationInMinutes={entry.durationInMinutes}
                  onChange={handleFormDataChange}
                />
              )}

              {/* Certification Checkbox */}
              <CertificationCheckbox
                isChecked={isCertified}
                onChange={(isChecked) => setIsCertified(isChecked)}
              />

              {/* Button Group */}
              <ButtonGroup
                isFormValid={isFormValid}
                formData={formData}
                onSubmit={handleSubmit}
                entryType={entry.type}
              />
            </>
          )}

          {/* Handle case when there's no error and no entry */}
          {/* This implies a new Paycode entry is being created for the sub */}
          {!queryError && !entry && (
            <>
              {/* Reasons */}
              <PaycodeReasons
                reason={formData?.type === "Paycode" ? formData.paycode : undefined}
                onReasonChange={handlePaycodeReasonChange}
              />

              {/* Date */}
              <Card>
                <div
                  css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                    {translate("LABEL_DATE")}
                    <Asterisk />
                  </p>
                  {/* Can only select within the current interval range */}
                  <DateInput
                    value={getEntryDate(entry)}
                    onChange={handleDateChange}
                    disabledDateRanges={[
                      { start: "1900-01-01", end: previousDay },
                      { start: nextDay, end: "2100-12-31" },
                    ]}
                  />
                </div>
              </Card>

              {/* Time Input Form */}
              <TimecardEntryForm entryType="Paycode" onChange={handleFormDataChange} />

              {/* Certification Checkbox */}
              <CertificationCheckbox
                isChecked={isCertified}
                onChange={(isChecked) => setIsCertified(isChecked)}
              />

              {/* Button Group */}
              <ButtonGroup
                isFormValid={isFormValid}
                formData={formData}
                onSubmit={handleSubmit}
                entryType="Paycode"
              />
            </>
          )}
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

/********* Types ********/
export const PunchType = {
  PunchIn: "PUNCH_IN",
  PunchOut: "PUNCH_OUT",
} as const;

type PunchType = (typeof PunchType)[keyof typeof PunchType];

export type Punch = {
  // FIXME: id is optional for the POST API
  id?: string;
  ts: string;
  type: PunchType;
};

type Request = {
  id: string;
  startTime: string;
  endTime: string;
};

type School = {
  id: string;
  name: string;
  timeZone: string;
};

export type Timecard = {
  type: "Timecard";
  date: string;
  request: Request;
  school: School;
  punches: Array<Punch>;
};

export type Paycode = {
  type: "Paycode";
  paycode: PaycodeReason;
  id: string;
  durationInMinutes: number;
  date?: string; //TODO: This is really only for the POST API
};

type Timesheet = Array<Timecard | Paycode>;

function RequestDetails(props: {
  schoolName: string;
  date: string;
  startTime: string;
  endTime: string;
}) {
  const { schoolName, date, startTime, endTime } = props;

  const shouldRender = schoolName || date || startTime || endTime;

  if (!shouldRender) {
    return null;
  }

  return (
    <Card>
      {schoolName && <p css={{ margin: 0 }}>{schoolName}</p>}

      {date && (
        <p css={{ margin: 0, ...fontSmall({ fontWeight: 600 }), lineHeight: "24px" }}>
          {DateTime.fromISO(date).toFormat("ccc, LLL d")}
        </p>
      )}

      {(startTime || endTime) && (
        <p css={{ margin: 0, ...fontXSmall() }}>
          {startTime && DateTime.fromISO(startTime).toFormat("h:mm a").toLowerCase()}
          {startTime && endTime && " - "}
          {endTime && DateTime.fromISO(endTime).toFormat("h:mm a").toLowerCase()}
        </p>
      )}
    </Card>
  );
}

type DateProps = {
  disabledDateRanges?: Array<{ start: string; end: string }>;
  value?: string;
  onChange: (date: string) => void;
  isDisabled?: boolean;
};

function Date(props: DateProps) {
  const { disabledDateRanges, value, onChange, isDisabled } = props;

  return (
    <Card>
      <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
          {translate("LABEL_DATE")}
          <Asterisk />
        </p>

        {/* Can only select within the current interval range */}
        <DateInput
          value={value}
          onChange={onChange}
          disabledDateRanges={disabledDateRanges}
          isDisabled={isDisabled}
        />
      </div>
    </Card>
  );
}

function CertificationCheckbox(props: {
  isChecked: boolean;
  onChange: (isChecked: boolean) => void;
}) {
  const { isChecked, onChange } = props;

  return (
    <Card>
      {/* Offset Card padding since Checkbox has its own */}
      <div css={{ marginLeft: -16, marginRight: -16 }}>
        <Checkbox
          label={translate("TIMECARD_ENTRY_CERTIFICATION_CHECKBOX_LABEL")}
          isChecked={isChecked}
          onCheckedChange={onChange}
        />
      </div>
    </Card>
  );
}

type ButtonGroupProps = {
  isFormValid: boolean;
  formData: Timecard | Paycode | undefined;
  onSubmit: (formData: Timecard | Paycode | undefined) => void;
  entryType: "Timecard" | "Paycode";
};

function ButtonGroup(props: ButtonGroupProps) {
  /*** Props ***/
  const { isFormValid, formData, entryType } = props;

  /*** Hooks ***/
  const history = useHistory();
  const { weekId } = useParams<URLParams>();

  /*** Helpers ***/
  const hasCancelButton = entryType === "Paycode";

  /*** Render ***/
  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: 16,
        ...(hasCancelButton && {
          [Breakpoints.mediumDeviceAndUp]: {
            flexDirection: "row-reverse", // primary on right, cancel on left
            justifyContent: "space-between",
            alignItems: "flex-start",
          },
        }),
      }}
    >
      {/* Submit */}
      <Button
        type="primary"
        onClick={() => {
          if (!isFormValid) return;
          console.log("submit", formData);
          // TODO: Submit to API when ready
          // onSubmit(formData);
        }}
        isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
        // TODO: Add loading state when submitting to API
        status={!isFormValid ? "disabled" : "enabled"}
      >
        {translate("TIMESHEET_SUBMIT_ENTRY_LABEL")}
      </Button>
      {/* Cancel - only show for Paycode type */}
      {hasCancelButton && (
        <Button
          type="secondary"
          isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
          // Go back to the timesheet list
          onClick={() => history.push(createTimesheetUrl(weekId))}
        >
          {translate("LABEL_CANCEL")}
        </Button>
      )}
    </div>
  );
}
